import React from 'react';
import PropTypes from 'prop-types';
import lazyGettext from 'js-utils/lazy-gettext';

import CheckMark from '../components/CheckMark';
import { PACKAGE_ONE } from './main';

export { EVENT_IMAGE_PLACEHOLDER_URL } from '@eb/organizations-events';

const INTEGRATIONS_FEATURE = lazyGettext(
    'Integrations to sell on your website',
);
const CUSTOMIZABLE_ORDER_FORM_FEATURE = lazyGettext('Customizable order form');
const REPORTING_FEATURE = lazyGettext(
    'Reporting by ticket type and across events',
);
const PAYOUT_BEFORE_EVENT_FEATURE = lazyGettext('Payout before your event');
const RESERVED_SEATING_FEATURE = lazyGettext('Reserved seating');
const SUPPORT_FOR_CREATORS_FEATURE = lazyGettext(
    'Phone, chat, and email support for event creators with paid events',
);

const NUMBER_OF_TICKET_TYPES_FEATURE = lazyGettext('Number of ticket types');
const UNLIMITED_TICKETS = lazyGettext('Unlimited');

export const DOWNGRADE_BASE = {
    features: [
        { feature: INTEGRATIONS_FEATURE },
        { feature: CUSTOMIZABLE_ORDER_FORM_FEATURE },
        { feature: REPORTING_FEATURE },
        { feature: PAYOUT_BEFORE_EVENT_FEATURE },
        { feature: RESERVED_SEATING_FEATURE },
        { feature: SUPPORT_FOR_CREATORS_FEATURE },
    ],
};

export const DOWNGRADE_V1 = {
    heading: lazyGettext('Considering downgrading to Essentials'),
    description: lazyGettext(
        'Make sure you understand how your Eventbrite experience will change.',
    ),
    features: [
        {
            feature: NUMBER_OF_TICKET_TYPES_FEATURE,
            professional: UNLIMITED_TICKETS,
            essentials: '1',
        },
    ],
};

export const PACKAGE_DOWNGRADE_V1 = {
    heading: lazyGettext('This change will remove Professional features'),
    description: lazyGettext(
        'To keep unlimited tickets types, full reporting, and more, you can keep your Professional package.',
    ),
};

export const DOWNGRADE_V2 = {
    heading: lazyGettext('Before you downgrade, compare Essentials'),
    description: lazyGettext(
        'Make sure you understand how your Eventbrite experience will change.',
    ),
    features: [
        { feature: lazyGettext('Ticket Sales on Facebook') },
        { feature: lazyGettext('Email Campaigns') },
        { feature: lazyGettext('Distribution on Partner sites') },
    ],
};

export const getHeading = (
    useNewVersion = false,
    isPackageDowngradeEnabled = false,
) => {
    let heading;
    let description;

    if (useNewVersion) {
        ({ heading, description } = DOWNGRADE_V2);
    } else {
        ({ heading, description } = isPackageDowngradeEnabled
            ? PACKAGE_DOWNGRADE_V1
            : DOWNGRADE_V1);
    }

    return { heading, description };
};

const CONFIRM_PLAN_CHANGE_BASE_FEATURES = [
    {
        feature: INTEGRATIONS_FEATURE,
        currentPackage: <CheckMark />,
    },
    {
        feature: CUSTOMIZABLE_ORDER_FORM_FEATURE,
        currentPackage: <CheckMark />,
    },
    //TEMP REMOVING THIS FOR COVID-19
    // {
    //     feature: PAYOUT_BEFORE_EVENT_FEATURE,
    //     currentPackage: <CheckMark />,
    // },
    {
        feature: RESERVED_SEATING_FEATURE,
        currentPackage: <CheckMark />,
    },
    {
        feature: REPORTING_FEATURE,
        currentPackage: <CheckMark />,
    },
];

const CONFIRM_PLAN_CHANGE_V1_FEATURES = [
    {
        feature: NUMBER_OF_TICKET_TYPES_FEATURE,
        currentPackage: UNLIMITED_TICKETS,
        newPackage: '1',
    },
];

const SALES_ON_FACEBOOK_FEATURE = lazyGettext('Ticket Sales on Facebook');
const EMAIL_CAMPAINGS_FEATURE = lazyGettext('Email Campaigns');
const PARTNER_SITES_FEATURE = lazyGettext('Distribution on Partner sites');

const CONFIRM_PLAN_CHANGE_V2_FEATURES = [
    {
        feature: SALES_ON_FACEBOOK_FEATURE,
        currentPackage: <CheckMark />,
        newPackage: null,
    },
    {
        feature: EMAIL_CAMPAINGS_FEATURE,
        currentPackage: <CheckMark />,
        newPackage: null,
    },
    {
        feature: PARTNER_SITES_FEATURE,
        currentPackage: <CheckMark />,
        newPackage: null,
    },
];

const getConfirmPlanDowngradeFeatures = (useNewVersion) => {
    return [
        ...(useNewVersion
            ? CONFIRM_PLAN_CHANGE_V2_FEATURES
            : CONFIRM_PLAN_CHANGE_V1_FEATURES),
        ...CONFIRM_PLAN_CHANGE_BASE_FEATURES,
    ];
};

export const getFeatures = (
    useNewVersion = false,
    isPackageDowngradeEnabled = true,
) =>
    isPackageDowngradeEnabled
        ? getConfirmPlanDowngradeFeatures(useNewVersion)
        : [
              ...(useNewVersion
                  ? DOWNGRADE_V2.features
                  : DOWNGRADE_V1.features),
              ...DOWNGRADE_BASE.features,
          ];

export const defaultDowngradePackage = [
    {
        plan: PACKAGE_ONE,
        version: 1,
    },
];

export const PACKAGE_DOWNGRADE_BREADCRUMB = {
    path: '#',
    label: lazyGettext('Downgrade'),
};

export const NEW_EVENTS_TEXT = lazyGettext(
    'Eventbrite Essentials will only apply to new events',
);
export const NO_COPY_TEXT = lazyGettext(
    "Previously created events can't be copied",
);
export const TABLE_TITLE = lazyGettext(
    "You'll have fewer features to boost your sales",
);
export const TABLE_TITLE_SHORT = lazyGettext('Features');
export const CURRENT_PACKAGE = lazyGettext('Current Package');
export const AFTER_DOWNGRADE = lazyGettext('After Downgrade');
export const KEEP_PROFESSIONAL_CTA = lazyGettext('Keep Professional');
export const DOWNGRADE_CTA = lazyGettext('Downgrade to Essentials');

export const MAX_CONFLICT_WITHOUT_PAGINATION = 5;
export const DEFAULT_PAGE_NUMBER = 1;

export const CONFLICTS_TRANSLATIONS = {
    header: (eventNumber = 0) => ({
        heading: lazyGettext(
            '%(eventNumber)s of your events will stay on Professional',
            { eventNumber },
        ),
        description: lazyGettext(
            "The following events can't be changed to Essentials because they already use Professional features and pricing. All other events and all future events can use Essentials.",
        ),
    }),
    table: {
        firstColumn: lazyGettext("Events that can't be changed"),
        lastColumn: lazyGettext('Professional features'),
        guidanceText: lazyGettext('Learn more about'),
        guidanceTextLink: lazyGettext('packages'),
        guidanceLink: '/support/articleredirect?anum=40418',
    },
};

export const API_STATUS_DRAFT = 'draft';
export const API_STATUS_LIVE = 'live';
export const API_STATUS_STARTED = 'started';

export const STATUS_DRAFT_TEXT = lazyGettext('Draft');
export const STATUS_LIVE_TEXT = lazyGettext('Published');
export const STATUS_STARTED_TEXT = lazyGettext('On Sale');

export const EVENT_SALES_STATUS_ICON_GREEN_COLOR = 'ui-green';

export const EVENT_SALES_STATUS_ICON_COLOR_MAP = {
    [API_STATUS_STARTED]: EVENT_SALES_STATUS_ICON_GREEN_COLOR,
    [API_STATUS_LIVE]: EVENT_SALES_STATUS_ICON_GREEN_COLOR,
};

export const EVENT_STATUS_LABEL = {
    [API_STATUS_DRAFT]: STATUS_DRAFT_TEXT,
    [API_STATUS_LIVE]: STATUS_LIVE_TEXT,
    [API_STATUS_STARTED]: STATUS_STARTED_TEXT,
    default: '',
};

export const MAP_CONFLICTS_TO_TRANSLATION = {
    ['event.add_ons']: lazyGettext('Add-ons'),
    ['organization.email_campaigns']: lazyGettext('Email campaign'),
    ['event.link_event_to_facebook']: lazyGettext('Ticket sales on Facebook'),
    ['event.order_form']: lazyGettext('Customizable attendee info collection'),
    ['event.reserved_seating']: lazyGettext('Reserved seating'),
    ['organization.scheduled_publish_event']: lazyGettext('Scheduled Publish'),
    ['event.unlimited_tickets']: lazyGettext('Multiple ticket types'),
    ['not_allowed']: lazyGettext('Downgrade not allowed from current plan'),
};

export const CONFLICT_FOOTER_PROPS_TYPES = {
    pageSize: PropTypes.number.isRequired,
    pageNumber: PropTypes.number.isRequired,
    pageCount: PropTypes.number.isRequired,
    objectCount: PropTypes.number.isRequired,
};

export const CONFLICTS_PROP_TYPES = PropTypes.shape({
    pagination: PropTypes.shape(CONFLICT_FOOTER_PROPS_TYPES),
    conflicts: PropTypes.arrayOf(
        PropTypes.shape({
            event: PropTypes.objectOf(PropTypes.any),
            downgradeConflicts: PropTypes.arrayOf(PropTypes.string),
        }),
    ),
});

export const PAGE_CONFLICTS_PROP_TYPES = PropTypes.shape({
    pagination: PropTypes.shape(CONFLICT_FOOTER_PROPS_TYPES),
    downgradeConflicts: PropTypes.objectOf(PropTypes.any),
});

export const PACKAGE_ONE_QUERY_STRING = 'package1';
